import React from "react"

export const getMatchFitWellbeingCategory = displayScore => {
  if (displayScore <= 50) {
    return "Languishing"
  }

  if (displayScore <= 65) {
    return "Surviving"
  }

  if (displayScore < 85) {
    return "Striving"
  }

  return "MatchFit"
}

export const generateMatchFitParagraphs = categorySlug => {
  if (categorySlug === "physical_recovery_metric") {
    return (
      <>
        <p>
          Physical Recovery is an assessment of how well you
          {" "}
          <b>Sleep</b>
          , your level of
          {" "}
          <b>Energy</b>
          {" "}
          and time you dedicate to
          {" "}
          <b>Recovery Activities</b>
          , which effects parasympathetic modulation (relaxation) and is essential to sustaining energy and reducing the risk of burnout and fatigue.
        </p>
        <p>It is well accepted that the human body thrives in response to positive stressors as they result in motivation and accomplishment. Without adequate recovery stress reactions accumulate, resulting in decreased cognitive ability and increased risk of burnout and physical and mental morbidity (the condition of having a disease).  Finding the balance between stress and recovery can be a fine line for high performers yet is an essential component that underpins the ability to reach your optimal levels of performance and wellbeing.</p>
      </>
    )
  }

  if (categorySlug === "physical_functioning_metric") {
    return (
      <>
        <p>
          Physical Functioning is an assessment of
          {" "}
          <b>Posture, Balance</b>
          {" "}
          and
          {" "}
          <b>Flexibility</b>
          {" "}
          and refers to the ability to perform basic activities of daily living. Physical function is an important predictor of health outcomes in older adults. The capacity to perform basic physical functions is a central aspect of health-related quality of life and a key predictor of hospitalisation, surgical outcomes, and mortality.
        </p>
        <p>Physical Functioning refers to mobility, balance, and postural alignment.  These influence the positions adopted during daily tasks, the ability to move freely, and stability throughout the day. Better functional abilities relate to lower risk of injury, falls, and bodily pain, as well as greater movement control and movement competency.</p>
      </>
    )
  }

  if (categorySlug === "nutrition_performance_metric") {
    return (
      <>
        <p>Nutrition underpins your cellular function, metabolism, energy levels and brain function. Many people do not understand how nutrition affects all aspects of the body and brain. A healthy eating pattern is fundamental to brain and body wellbeing, as optimal nutrition benefits almost every aspect of your health.</p>
        <p>The Nutrition Barometer assesses your knowledge and modifiable behaviours associated with high performance and wellbeing. These factors include hydration, alcohol and caffeine consumption, intake of vegetables, protein, fats, carbohydrates, sugar and processed foods, as well as eating and cooking habits. Even if you believe you have a varied and healthy diet, there is always room for improvement and/or variety.</p>
      </>
    )
  }

  if (categorySlug === "movement_metric") {
    return (
      <>
        <p>
          Movement is an assessment of
          {" "}
          <b>Cardio-Respiratory Fitness, Daily Steps, Resistance Training</b>
          {" "}
          and
          {" "}
          <b>Flexibility</b>
          {" "}
          Physical activity can take many different forms and occur in multiple locations. The MatchFit Calculator doesn’t only monitor formal exercise, it accounts for movement across the day including mobility, flexibility and strength.
        </p>
        <p>Regular formal (structured) exercise does not guarantee a high score in this category. Some people fall into the category of ‘the active couch potato’ exercising hard a few times each week then sitting around the rest of the time. The objective is to include both formal exercise and incidental movement throughout the day.</p>
      </>
    )
  }

  if (categorySlug === "social_connectedness_metric") {
    return (
      <>
        <p>
          Social Connectedness is an assessment of your sense of
          {" "}
          <b>Isolation/Loneliness, Relationship Satisfaction</b>
          {" "}
          and sense of
          {" "}
          <b>Social Support.</b>
        </p>
        <p>Humans crave connection. For thousands of years, we have lived and worked in communities - connection is fundamental to the way we experience the world. Being truly connected provides purpose and gives our lives meaning and pleasure.</p>
        <p>Social connectedness measures aspects of loneliness, relationship satisfaction and perceived social support. Mean- ingful relationships and a sense of belonging are critical for wellbeing in an age of constant mobility and connectivity. Although technology has made people more digitally connected than ever, more people in society are reporting that they feel alone. Loneliness activates physical and psychological stress responses, depresses the immune system, increases risk of cardiovascular disease and represents as great a risk for long-term health and longevity as smoking. Conversely, social support and relationship satisfaction, especially a good network of friends and a healthy marriage/stable relationship are associated with better health, lower premature mortality and longevity.</p>
      </>
    )
  }

  if (categorySlug === "psychological_performance_metric") {
    return (
      <>
        <p>
          Psychological Performance incorporates
          {" "}
          <b>Flow, Self Actualisation/Mastery</b>
          {" "}
          , sense of
          {" "}
          <b>Focus</b>
          {" "}
          sense of
          {" "}
          <b>Control.</b>
        </p>
        <p>People with a greater sense of purpose and direction are more likely to outlive their peers, have more positive feelings and lower physical symptoms, and earn higher incomes compared to those with a low sense of life purpose. Focus is considered most important during stressful or anxiety-inducing tasks where pressure/anxiety creates a distraction and shifts attention to worrying about the situation and its consequences, a cognitive load that can decrease performance. Perceived control relates to the ability to decide for yourself what to do and the ability to take time to contemplate and reflect, gaining greater insight into feelings. Greater perceived con- trol is associated with high levels of optimism, lower psychological stress and decreased risk of professional burnout.</p>
      </>
    )
  }

  if (categorySlug === "emotional_wellbeing_metric") {
    return (
      <>
        <p>
          Emotional Wellbeing is an assessment of
          {" "}
          <b>Satisfaction with Life and Mood, Happiness, Depression and Anxiety Symptoms</b>
          {" "}
          and your
          {" "}
          <b>Stress Coping.</b>
        </p>
        <p>Emotional wellbeing is the ability to produce positive emotions, moods, thoughts, and feelings, and adapt when con- fronted with adversity and stressful situations. Resilience and the ability to bounce back/reframe after challenging life events is a core foundation of Emotional Wellbeing.</p>
        <p>Emotional wellbeing is a very important part of holistic wellbeing and impacts outlook on life, relationships, and mental health. Emotional Wellbeing is learning to respond in a calm and measured way, rather than immediately reacting or flying off the handle. This involves thinking through what you want to happen in an interaction involving a challenging conversation or conflict. High levels of emotional wellbeing enable you to be measured, thoughtful, and provides a foundation for creativity and adaptability.</p>
      </>
    )
  }

  if (categorySlug === "cognitive_flex_metric") {
    return (
      <>
        <p>
          Cognitive flexibility is the ability to achieve goals/tasks by adapting mental models and behaviours to new envi- ronments. This includes the ability to switch between different tasks and corresponding behavioural responses. Cognitive Flexibility includes
          {" "}
          <b>Growth Mindset, Openness and Adaptability to Change</b>
          {" "}
          and
          {" "}
          <b>Grit.</b>
        </p>
        <p>Cognitive Flexibility provides the ability to see that what we are doing that is/is not leading to success and to sustain/make appropriate changes. Cognitive flexibility can have a positive effect on inherent biases and is associated with higher resilience to negative life events, as well as better quality of life as we get older. Cognitive flexibility expands sense of awareness, increases ability to quickly adapt to new situations and increases your likelihood of success.</p>
      </>
    )
  }

  if (categorySlug === "psychological_detachment_metric") {
    return (
      <>
        <p>
          Psychological Detachment is an assessment of your ability to
          {" "}
          <b>Switch Off</b>
          {" "}
          from work out of hours, allocate time for
          {" "}
          <b>Play</b>
          {" "}
          activities and spending time in
          {" "}
          <b>Nature.</b>
        </p>
        <p>Psychological detachment refers to not constantly working or thinking about job-related issues after work. Lower levels of psychological detachment from work are associated with a higher proneness to exhaustion and reduced levels of work engagement the next day. Psychological detachment is essential to achieving sustainable high performance at work and to maintaining optimum health and wellbeing. Regular doses of nature enhance mental health, reduce stress levels and improve relaxation and wellbeing.</p>
      </>
    )
  }

  return null
}

export const MFC_CATEGORIES = {
  biological_age_metric: {
    heading: "Biological Age",
    categoryType: "Body",
    assetName: "MFC-Bio-age",
    goal: "Biological Age at least five years below Chronological Age i.e. 5 years younger biologically",
  },
  physical_functioning_metric: {
    heading: "Physical Functioning",
    categoryType: "Body",
    assetName: "MFC-Physical-following",
    goal: "Scores above 85 percent. You have a strong physical foundation.",
  },
  physical_recovery_metric: {
    heading: "Physical Recovery",
    categoryType: "Body",
    assetName: "MFC-Physical-recovery",
    goal: "Scores above 85 percent. Ensure you wake feeling refreshed and allocate specific time to recovery activities.",
  },
  nutrition_performance_metric: {
    heading: "Nutrition Performance",
    categoryType: "Body",
    assetName: "MFC-Nutrition-performance",
    goal: "Scores above 85 percent. You are eating a healthy balance diet.",
  },
  movement_metric: {
    heading: "Movement",
    categoryType: "Body",
    assetName: "MFC-Movement",
    goal: "Scores above 85 percent. At least: 10,000 steps per day; 150 minutes of exercise per week; two sessions of resistance training per week; three sessions of flexibility training per week.",
  },
  social_connectedness_metric: {
    heading: "Social Connectedness",
    categoryType: "Brain",
    assetName: "MFC-Social-connectedness",
    goal: "Scores above 85 percent. You are satisfied with life and cope well with stress.",
  },
  psychological_performance_metric: {
    heading: "Psychological Performance",
    categoryType: "Brain",
    assetName: "MFC-Psychological-performance",
    goal: "Scores above 85 percent. You have a clear sense of purpose and are able to focus and control your peak performance.",
  },
  emotional_wellbeing_metric: {
    heading: "Emotional Wellbeing",
    categoryType: "Brain",
    assetName: "MFC-Emotional-wellbeing",
    goal: "Scores above 85 percent. You are satisfied with life and cope well with stress.",
  },
  cognitive_flex_metric: {
    heading: "Cognitive Flexibility",
    categoryType: "Brain",
    assetName: "MFC-Cognitive-flexibility",
    goal: "Scores above 85 percent. You are open to change, learning and adaptation.",
  },
  psychological_detachment_metric: {
    heading: "Psychological Detachment",
    categoryType: "Brain",
    assetName: "MFC-Psychological-Detachment",
    goal: "Scores above 85 percent. You make time to relax and switch of from thinking about work.",
  },
}

export const MFC_INSTRUCTIONS_ICN = {
  waist: "Measure@2x",
  pushups_m: "Push-Ups-M@2x",
  pushups: "Push-Ups-F@2x",
  sit_to_stands: "Sit-to-Stand@2x",
  right_leg_balance: "Balance@2x",
  right_arm_back: "Reach@2x-Flipped",
  left_arm_back: "Reach@2x",
  touch_toes: "Stretch@2x",
  heart_rate: "Heartrate@2x",
  heart_rate_manually: "Heartrate@2x"
}

export const LLC_INSTRUCTIONS_ICN = {
  ...MFC_INSTRUCTIONS_ICN,
  pushups: "Pushup_f@2x",
  pushups_m: "Pushup_m@2x"
}